import { message } from '@flowus/common/components/message';
import { VITE_PREFIX } from '@flowus/common/const';
import type { ModalSchema } from '@flowus/common/next-modal';
import { BlockType } from '@next-space/fe-api-idl';
import type { FC } from 'react';
import { useState } from 'react';
import { Button } from 'src/common/components/button';
import { Input } from 'src/common/components/input';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { VITE_NOTION_CLIENT_ID } from 'src/env';
import { ViewPath } from 'src/utils';
import { sequence } from 'src/utils/async-utils';
import { enqueueTask } from 'src/utils/tasks';
import { TocType } from 'src/views/main/aside/toc/types';
import { useCommon } from './hook';
import { cx } from '@flowus/common/cx';
import { publicLink } from 'src/common/const';
import { Icon } from 'src/common/components/icon';

export const ImportNotion: FC<ModalSchema.CloseModal> = () => {
  const { t } = useAppTranslation();
  const {
    createDocPage,
    fetchPage,
    currentSpace,
    closeAllModal,
    history,
    openLoading,
    closeLoading,
  } = useCommon();

  const [id, setId] = useState('');

  const onClick = () => {
    const width = 800;
    const height = 800;

    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    const redirectUri = encodeURIComponent(`${VITE_PREFIX}${ViewPath.notion}`);

    window.open(
      `https://api.notion.com/v1/oauth/authorize?client_id=${VITE_NOTION_CLIENT_ID}&response_type=code&owner=user&redirect_uri=${redirectUri}`,
      'notion',
      `width=${width},height=${height},left=${left},top=${top}`
    );
  };

  const onSubmit = () => {
    if (id.length !== 32) {
      message.error(t('components.import.notion.id_error'));
      return;
    }

    const pageId = createDocPage(BlockType.PAGE, { parentId: currentSpace.uuid }, TocType.PRIVATE);

    if (!pageId) return;

    openLoading(t('components.import.notion.cloud_importing'));

    void sequence(() =>
      enqueueTask(
        {
          eventName: 'import',
          request: {
            blockId: pageId,
            spaceId: currentSpace.uuid,
            importOptions: {
              type: 'notion',
              notionId: id,
            },
          },
        },
        {
          success: async ({ uuid }) => {
            uuid && void fetchPage(uuid, { force: true });
            closeLoading();
            closeAllModal();
            history.push(`/${pageId}`);
          },
          fail() {
            if (pageId) {
              closeLoading();
              closeAllModal();
              message.warning(t('components.import.notion.task_status_error'));
            }
          },
        }
      )
    );
  };
  return (
    <div className="w-[480px] p-6">
      <div className="w-full">
        <div className="flex items-center ">
          <CircleNo orderNum={1} className="self-start mt-0.5" />
          <div className="ml-2">
            <div className="text-t2-medium">{t('components.import.notion.authorize_page')}</div>
            <div className="text-grey2 text-t4 mt-1">
              {t('components.import.notion.skip_if_authorized')}
            </div>
            <Button className="mt-4 h-7 text-t2-medium" colorType="active" onClick={onClick}>
              {t('common.authorize')}
            </Button>
          </div>
        </div>
        <div className="flex items-center mt-6 w-full">
          <CircleNo orderNum={2} className="self-start mt-0.5" />
          <div className="ml-2 w-full">
            <div className="flex items-center">
              <div className="text-t2-medium">{t('components.import.notion.enter_page_id')}</div>
              <a target="_blank" href={publicLink.importFromNotion} className="animate-click">
                <Icon size="small" name="IcInviteHelp" className="text-grey3 ml-2" />
              </a>
            </div>
            <Input
              className="w-full mt-1"
              inputClassName="h-8 "
              placeholder={t('components.import.notion.enter_page_id')}
              onChange={setId}
              value={id}
            />
            <Button className="mt-4 h-7 text-t2-medium" colorType="active" onClick={onSubmit}>
              {t('common.import')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
const CircleNo: FC<{ orderNum: number; className?: string }> = (props) => {
  return (
    <div
      className={cx(
        'flex justify-center items-center rounded-full w-4 h-4 bg-active_color/20',
        props.className
      )}
    >
      <span className=" text-active_color text-t4">{props.orderNum}</span>
    </div>
  );
};
